/* eslint-disable max-lines */
import { editLanguageMatcher, receiveNewKeyword } from 'actions/identifier';
import SelectRedesign from 'components/Select/SelectRedesign';
import WarningMsg from 'components/WarningMsg';
import { platformTypeOptions } from 'constants/annotator';
import { platformNames } from 'constants/platform';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveLanguageMatcher,
  getActiveLanguageMatcherId,
  getCompareMode,
  getShouldDisableEdit,
  saveIdentifierRequestLoading,
  unsavedChanges,
} from 'selectors/identifier';
import { useSelector } from 'store';
import { languageMatcherNotificationMsgs } from 'utils/userNotificationMsgs';
import DeleteMatcherButton from '../DeleteMatcherButton';
import LanguageMatcherHeader from '../LanguageMatcherHeader';
import ListFieldWithImport from '../shared/ListFieldWithImport';
import MultiSelectField from '../shared/MultiSelectField';

type Props = { isSaved: boolean };

const platformOptions = Object.entries(platformNames)
  .filter(([k]) => k !== 'all')
  .map(([value, label]) => ({ value, label }));

const MetadataGeneralForm: React.FC<Props> = ({ isSaved }) => {
  const dispatch = useDispatch();
  const unsaved = useSelector(unsavedChanges);
  const isLoadingSaveIdentifier = useSelector(saveIdentifierRequestLoading);
  const compareMode = useSelector(getCompareMode);
  const shouldDisable = useSelector(getShouldDisableEdit);
  const activeLanguageMatcher = useSelector(getActiveLanguageMatcher);
  const activeLanguageMatcherId = useSelector(getActiveLanguageMatcherId);

  const setPlatform = (platform: string): void => {
    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'metadata_communication',
        data: { key: 'platform', value: platform },
      })
    );
    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'metadata_communication',
        data: { key: 'saved', value: false },
      })
    );
  };

  const onEditLanguageMatcher = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!activeLanguageMatcher.uuid || e.target.value === null) return;
    dispatch(
      editLanguageMatcher({
        languageMatcherId: activeLanguageMatcher.uuid,
        data: { [e.target.name]: e.target.value },
      })
    );
  };

  const setIsList = (value: boolean): void => {
    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'metadata_communication',
        data: { key: 'is_list', value },
      })
    );
    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'metadata_communication',
        data: { key: 'saved', value: false },
      })
    );
  };

  return (
    <div className="flex flex-col pt-5 pl-10 pr-4 pb-6 text-body h-full">
      <div className="flex flex-row justify-end">
        <WarningMsg
          message={languageMatcherNotificationMsgs.unsavedChanges}
          showLoading={isLoadingSaveIdentifier}
          showWarning={unsaved}
          showSavedMsg={isSaved}
        />
      </div>

      <div className="-mt-4 flex flex-col gap-4 text-body h-full">
        <LanguageMatcherHeader />

        <div className="mt-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1 w-124">
            <span className="text-body">Key to match</span>
            <input
              name="key"
              id="key"
              type="text"
              data-testid="metadata-general-key-input"
              // @ts-ignore
              value={activeLanguageMatcher.key ?? ''}
              onChange={onEditLanguageMatcher}
              className="form-input w-full h-9 py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body focus:outline-none"
              disabled={compareMode || shouldDisable}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-body">The key (above) is a list of values</span>
            <div className="flex flex-row gap-4">
              <label htmlFor="is-list-no" className="flex flex-row items-center gap-2">
                <input
                  type="radio"
                  name="is-list"
                  id="is-list-no"
                  value="no"
                  className="input-radio"
                  // @ts-ignore
                  checked={!activeLanguageMatcher.is_list}
                  onChange={(): void => setIsList(false)}
                  disabled={compareMode || shouldDisable}
                />
                No
              </label>
              <label htmlFor="is-list-yes" className="flex flex-row items-center gap-2">
                <input
                  type="radio"
                  name="is-list"
                  id="is-list-yes"
                  value="yes"
                  className="input-radio"
                  // @ts-ignore
                  checked={Boolean(activeLanguageMatcher.is_list)}
                  onChange={(): void => setIsList(true)}
                  disabled={compareMode || shouldDisable}
                />
                Yes
              </label>
            </div>
          </div>

          <div className="flex flex-row gap-4">
            <div className="flex flex-col gap-1 w-60">
              <span className="text-body">Platform</span>
              <SelectRedesign
                className="h-9.5 rounded-lg bg-white"
                valueClassName="h-9.5 py-2 px-2"
                placeholder="Platform"
                data-testid="platform-select-single"
                options={platformOptions.map((o) => ({
                  ...o,
                  disabled: compareMode || shouldDisable,
                }))}
                onChange={(value): void => setPlatform(String(value))}
                // @ts-ignore
                value={(activeLanguageMatcher.platform as string | undefined) || ''}
              />
            </div>

            <MultiSelectField
              label="Platform type"
              languageMatcherId={activeLanguageMatcherId}
              parentType="metadata_communication"
              // @ts-ignore
              field="communication_kind"
              options={platformTypeOptions}
              // @ts-ignore
              value={activeLanguageMatcher.communication_kind as string[] | undefined}
              widthClassName="w-60"
              compareMode={compareMode}
              shouldDisable={shouldDisable}
            />
          </div>

          <ListFieldWithImport
            label="Values to match"
            languageMatcherId={activeLanguageMatcherId}
            parentType="metadata_communication"
            // @ts-ignore
            field="values"
            // @ts-ignore
            value={(activeLanguageMatcher.values as string[]) || []}
            compareMode={compareMode}
            shouldDisable={shouldDisable}
            enableImport={false}
            widthClassName="w-124"
          />
        </div>

        <div className="mt-auto pt-6 flex flex-row justify-end">
          <DeleteMatcherButton
            text="Delete Matcher"
            setValue={(): void => {
              // moot
            }}
            setHighlightedLanguageMatcher={(): void => {
              // moot
            }}
            activeLanguageMatcher={activeLanguageMatcher}
          />
        </div>
      </div>
    </div>
  );
};

export default MetadataGeneralForm;
